import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Move,
  Maximize2,
  Minimize2,
  Pencil,
  Check,
  FlipHorizontal2,
} from "lucide-react";
import {
  ISO_W,
  ISO_H,
  CH,
  GAP,
  STRIDE,
  ZOOM_MIN,
  ZOOM_MAX,
  isoScreen,
  screenToTile,
  drawPos,
  chunkOrigin,
  loadTile,
  tileOk,
  preloadAll,
  GROUND_TYPES,
  isCubeTile,
  CUBE_H,
} from "./tileset";
import { BY_ID } from "./catalog";
import { drawIsoBridge, drawCloud } from "./sprites";
import { makeWalker, updateAgents, drawBubble } from "./npc";
import { drawSeaBase, buildSeaDecor } from "./water";
import {
  currentPhase,
  currentDayIndex,
  isRainyDay,
  skyAt,
  makeRain,
  drawRain,
} from "./sky";
import { loadOverrides, overrideFor, overrideImage } from "./overrides.js";
import { loadCustomItems } from "./customItems.js";
import { fmt } from "./economy";
// Alias `tr` : dans ce fichier, `t` designe deja le temps d'animation
// (ecoule en secondes) a l'interieur de frame(). On garde `t` pour la
// traduction dans le JSX en bas du composant (aucun conflit la-bas),
// et on utilise `tr` partout ou un `t` local de temps existe deja.
import { t, t as tr } from "@/i18n";

const key = (x, y) => `${x},${y}`;
const hash = (x, y) => {
  const h = Math.imul(x * 374761393 + y * 668265263, 1274126177);
  return ((h ^ (h >>> 15)) >>> 0) / 4294967296;
};

const CORNERS = [
  [0, 0],
  [CH - 1, 0],
  [0, CH - 1],
  [CH - 1, CH - 1],
];
export function chunkCutouts(cx, cy) {
  const out = new Set();
  CORNERS.forEach(([lx, ly], i) => {
    if (hash(cx * 31 + i, cy * 17 + i * 7) > 0.55) out.add(key(lx, ly));
  });
  return out;
}

export function partsOf(item, flip) {
  const base = item.parts || [{ tile: item.tile, dx: 0, dy: 0 }];
  if (!flip) return base;
  const w = (item.size || [1, 1])[0];
  return base.map((p) => ({ ...p, dx: w - 1 - p.dx }));
}

export default function IsoIsland({
  island = { chunks: [], placed: [], terrain: {} },
  editMode,
  pending,
  pendingFlip = false,
  paintGround,
  balance,
  chunkCost,
  movingUid,
  onPlace,
  onSelectPlaced,
  onBuyChunk,
  onPaint,
  selectedPlaced,
  onDropItem,
  onCancelAction,
  onExitEditor,
  onToggleEdit,
  onFlipPending,
  calibrate,
  adminSky,
  readOnly = false,
  overlay = null,
  onFullscreenChange,
}) {
  const shellRef = useRef(null);
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const glowRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [zoom, setZoom] = useState(0.5);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [hover, setHover] = useState(null);
  const [showTouchWarning, setShowTouchWarning] = useState(false);

  const drag = useRef(null);
  const tap = useRef(null);
  const pointers = useRef(new Map());
  const pinch = useRef(null);
  const walkers = useRef(new Map());
  const birds = useRef([]);
  const seaLayer = useRef(null);
  const seaKey = useRef("");
  const rainDrops = useRef(makeRain());
  const warningTimer = useRef(null);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    Promise.all([preloadAll(), loadOverrides(), loadCustomItems()]).then(() =>
      setReady(true),
    );
  }, []);

  const owned = useMemo(
    () => new Set(island.chunks.map((c) => key(c.x, c.y))),
    [island.chunks],
  );

  const groundTiles = useMemo(() => {
    const arr = [];
    for (const c of island.chunks) {
      const { ox, oy } = chunkOrigin(c.x, c.y);
      const cut = chunkCutouts(c.x, c.y);
      for (let j = 0; j < CH; j++)
        for (let i = 0; i < CH; i++) {
          if (cut.has(key(i, j))) continue;
          arr.push([ox + i, oy + j]);
        }
    }
    return arr;
  }, [island.chunks]);

  const landSet = useMemo(
    () => new Set(groundTiles.map(([x, y]) => key(x, y))),
    [groundTiles],
  );
  const isLand = useCallback((tx, ty) => landSet.has(key(tx, ty)), [landSet]);

  const candidates = useMemo(() => {
    const out = new Map();
    for (const c of island.chunks)
      for (const [dx, dy] of [
        [0, -1],
        [1, 0],
        [0, 1],
        [-1, 0],
      ]) {
        const k = key(c.x + dx, c.y + dy);
        if (!owned.has(k)) out.set(k, { x: c.x + dx, y: c.y + dy });
      }
    return [...out.values()];
  }, [island.chunks, owned]);

  const bridges = useMemo(() => {
    const out = [];
    const mid = Math.floor(CH / 2);
    for (const c of island.chunks)
      for (const [dx, dy] of [
        [1, 0],
        [0, 1],
      ]) {
        if (!owned.has(key(c.x + dx, c.y + dy))) continue;
        const a = chunkOrigin(c.x, c.y);
        const b = chunkOrigin(c.x + dx, c.y + dy);
        const half = 0.44;
        out.push(
          dx
            ? {
                axis: "x",
                x0: a.ox + CH - 0.6,
                x1: b.ox - 0.4,
                ya: a.oy + mid - half,
                yb: a.oy + mid + half,
                ca: c,
                cb: { x: c.x + dx, y: c.y + dy },
              }
            : {
                axis: "y",
                y0: a.oy + CH - 0.6,
                y1: b.oy - 0.4,
                xa: a.ox + mid - half,
                xb: a.ox + mid + half,
                ca: c,
                cb: { x: c.x + dx, y: c.y + dy },
              },
        );
      }
    return out;
  }, [island.chunks, owned]);

  const cellsOf = (item, x, y) => {
    const [w, d] = item?.size || [1, 1];
    const out = [];
    for (let i = 0; i < w; i++)
      for (let j = 0; j < d; j++) out.push([x + i, y + j]);
    return out;
  };

  const occupied = useMemo(() => {
    const objets = new Map(),
      sols = new Map();
    for (const p of island.placed) {
      if (p.uid === movingUid) continue;
      const item = BY_ID[p.itemId];
      if (!item || item.layer === "pnj" || item.layer === "animal") continue;
      const target = item.layer === "sol" ? sols : objets;
      const [w, d] = item.size || [1, 1];
      for (let i = 0; i < w; i++)
        for (let j = 0; j < d; j++) target.set(key(p.x + i, p.y + j), p.uid);
    }
    return { objets, sols };
  }, [island.placed, movingUid]);

  const bridgeTiles = useMemo(() => {
    const out = [];
    const mid = Math.floor(CH / 2);
    for (const c of island.chunks)
      for (const [dx, dy] of [
        [1, 0],
        [0, 1],
      ]) {
        if (!owned.has(key(c.x + dx, c.y + dy))) continue;
        const a = chunkOrigin(c.x, c.y);
        for (let i = 0; i < GAP; i++) {
          out.push(
            dx ? [a.ox + CH + i, a.oy + mid] : [a.ox + mid, a.oy + CH + i],
          );
        }
      }
    return out;
  }, [island.chunks, owned]);

  const nav = useMemo(() => {
    const walkable = new Set(landSet);
    for (const [x, y] of bridgeTiles) walkable.add(key(x, y));
    for (const p of island.placed) {
      const item = BY_ID[p.itemId];
      if (!item || item.layer !== "objet") continue;
      const [w, d] = item.size || [1, 1];
      for (let i = 0; i < w; i++)
        for (let j = 0; j < d; j++) walkable.delete(key(p.x + i, p.y + j));
    }
    const nearestWalkable = (gx, gy) => {
      const sx = Math.round(gx),
        sy = Math.round(gy);
      for (let r = 1; r <= 6; r++)
        for (let dx = -r; dx <= r; dx++)
          for (let dy = -r; dy <= r; dy++) {
            if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue;
            if (walkable.has(key(sx + dx, sy + dy)))
              return { x: sx + dx, y: sy + dy };
          }
      return null;
    };
    return { walkable, nearestWalkable };
  }, [landSet, bridgeTiles, island.placed]);

  const canPlaceAt = useCallback(
    (tx, ty) => {
      if (!pending) return false;
      if (pending.layer === "pnj" || pending.layer === "animal")
        return nav.walkable.has(key(tx, ty));
      const target = pending.layer === "sol" ? occupied.sols : occupied.objets;
      for (const [cx, cy] of cellsOf(pending, tx, ty)) {
        if (!isLand(cx, cy)) return false;
        if (target.has(key(cx, cy))) return false;
      }
      return true;
    },
    [pending, isLand, occupied, nav],
  );

  const bobOf = (c, t) => Math.sin(t * 0.55 + (c.x * 1.7 + c.y * 2.3)) * 5;

  const islandBounds = useCallback((chunks) => {
    let minX = Infinity,
      maxX = -Infinity,
      minY = Infinity,
      maxY = -Infinity;
    for (const c of chunks) {
      const { ox, oy } = chunkOrigin(c.x, c.y);
      for (const [gx, gy] of [
        [ox - 0.5, oy - 0.5],
        [ox + CH - 0.5, oy - 0.5],
        [ox + CH - 0.5, oy + CH - 0.5],
        [ox - 0.5, oy + CH - 0.5],
      ]) {
        const { sx, sy } = isoScreen(gx, gy);
        minX = Math.min(minX, sx);
        maxX = Math.max(maxX, sx);
        minY = Math.min(minY, sy);
        maxY = Math.max(maxY, sy);
      }
    }
    return { minX, maxX, minY, maxY };
  }, []);

  const fitToIsland = useCallback(() => {
    const el = wrapRef.current;
    if (!el || !island.chunks.length) return;
    const r = el.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) return;

    const { minX, maxX, minY, maxY } = islandBounds(island.chunks);
    const spanX = maxX - minX + ISO_W;
    const spanY = maxY - minY + ISO_H * 1.6;

    const naturalFit = Math.min(r.width / spanX, r.height / spanY) * 0.95;
    const z = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, naturalFit));
    const cx = (minX + maxX) / 2,
      cy = (minY + maxY) / 2;
    setZoom(z);

    const halfH = ((maxY - minY) / 2) * z;
    const minFrac = halfH / r.height;
    const maxFrac = (r.height - halfH) / r.height;
    const frac = Math.max(minFrac, Math.min(maxFrac, 0.3));

    const panX = -cx * z,
      panY = r.height * frac - r.height * 0.32 - cy * z;
    setPan({ x: panX, y: panY });
  }, [island.chunks, islandBounds]);

  const fittedFor = useRef(-1);

  function overrideVisual(item) {
    const ov = item?.id ? overrideFor(item.id) : null;
    if (!ov) return null;
    const [w, d] = item.size || [1, 1];
    return {
      img: overrideImage(item.id),
      anchor: ov.anchorY,
      isCube: !!ov.isCube,
      mul: ov.scale || Math.max(1, w, d),
      w,
      d,
    };
  }

  function tileVisual(part) {
    return {
      img: loadTile(part.tile),
      anchor: part.tile,
      isCube: isCubeTile(part.tile),
      mul: 1,
    };
  }

  function pushItem(scene, LAYER, item, x, y, flip, extra) {
    const ov = overrideVisual(item);
    if (ov) {
      const gx = x + (ov.w - 1) / 2,
        gy = y + (ov.d - 1) / 2;
      scene.push({
        d: gx + gy,
        l: item.layer === "sol" ? LAYER.chemin : LAYER.bloc,
        kind: "tile",
        gx,
        gy,
        item,
        flip,
        img: ov.img,
        anchor: ov.anchor,
        mul: ov.mul,
        lift: ov.isCube && item.layer !== "sol" ? CUBE_H : 0,
        ...extra,
      });
      return;
    }
    for (const part of partsOf(item, flip)) {
      const gx = x + part.dx,
        gy = y + part.dy;
      const vis = tileVisual(part);
      scene.push({
        d: gx + gy,
        l: item.layer === "sol" ? LAYER.chemin : LAYER.bloc,
        kind: "tile",
        gx,
        gy,
        item,
        flip,
        img: vis.img,
        anchor: vis.anchor,
        mul: 1,
        lift: vis.isCube && item.layer !== "sol" ? CUBE_H : 0,
        ...extra,
      });
    }
  }

  const live = useRef(null);
  live.current = {
    island,
    editMode,
    pending,
    pendingFlip,
    paintGround,
    balance,
    chunkCost,
    selectedPlaced,
    zoom,
    pan,
    hover,
    groundTiles,
    bridges,
    candidates,
    nav,
    bridgeTiles,
    calibrate,
    isLand,
    canPlaceAt,
    occupied,
    adminSky,
    readOnly,
    fitToIsland,
  };

  useEffect(() => {
    if (!ready) return;
    const canvas = canvasRef.current;
    const glow = glowRef.current;
    const ctx = canvas.getContext("2d");
    const gctx = glow.getContext("2d");
    let raf,
      start = performance.now(),
      lastNow = start;
    const reduce = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    let dpr = Math.min(2, window.devicePixelRatio || 1);

    const resize = () => {
      if (!wrapRef.current) return;
      const r = wrapRef.current.getBoundingClientRect();
      if (r.width < 1 || r.height < 1) return;
      dpr = Math.min(2, window.devicePixelRatio || 1);
      for (const cv of [canvas, glow]) {
        const w = Math.max(1, Math.round(r.width * dpr));
        const h = Math.max(1, Math.round(r.height * dpr));
        if (cv.width !== w) cv.width = w;
        if (cv.height !== h) cv.height = h;
        cv.style.width = r.width + "px";
        cv.style.height = r.height + "px";
      }
    };
    resize();
    {
      const r0 = wrapRef.current?.getBoundingClientRect();
      if (r0 && r0.width >= 50 && r0.height >= 50) {
        live.current.fitToIsland?.();
        fittedFor.current = live.current.island.chunks.length;
      }
    }
    let lastW = 0,
      lastH = 0,
      refitTimer;
    const ro = new ResizeObserver(() => {
      resize();
      const r = wrapRef.current?.getBoundingClientRect();
      if (!r || r.width < 50 || r.height < 50) return;
      const sizeChanged =
        Math.abs(r.width - lastW) > 40 || Math.abs(r.height - lastH) > 40;
      const neverFitted =
        fittedFor.current !== live.current.island.chunks.length;
      if (sizeChanged || neverFitted) {
        lastW = r.width;
        lastH = r.height;
        clearTimeout(refitTimer);
        refitTimer = setTimeout(() => {
          live.current.fitToIsland?.();
          fittedFor.current = live.current.island.chunks.length;
        }, 120);
      }
    });
    ro.observe(wrapRef.current);

    let lastFrame = performance.now();
    const frame = (now) => {
      const cur = live.current;
      const dt = now - lastFrame;
      lastFrame = now;
      const phase = cur.adminSky?.phase ?? currentPhase();
      const dayIdx = currentDayIndex();
      const raining = cur.adminSky?.rain ?? isRainyDay(dayIdx);
      const M = skyAt(phase);
      const t = reduce ? 0 : (now - start) / 1000;
      const W = canvas.width,
        H = canvas.height;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      gctx.setTransform(1, 0, 0, 1, 0, 0);
      gctx.clearRect(0, 0, W, H);

      drawSeaBase(ctx, W, H, t, M.sea);

      const camX = W / 2 + cur.pan.x * dpr;
      const camY = H * 0.32 + cur.pan.y * dpr;
      const S = cur.zoom * dpr;
      ctx.save();
      ctx.translate(camX, camY);

      const proj = (gx, gy, dy = 0) => {
        const { sx, sy } = isoScreen(gx, gy);
        return { x: sx * S, y: sy * S + dy * S };
      };
      const chunkPoly = (c, dy = 0) => {
        const { ox, oy } = chunkOrigin(c.x, c.y);
        return [
          proj(ox - 0.5, oy - 0.5, dy),
          proj(ox + CH - 0.5, oy - 0.5, dy),
          proj(ox + CH - 0.5, oy + CH - 0.5, dy),
          proj(ox - 0.5, oy + CH - 0.5, dy),
        ];
      };

      const tint = Math.round(phase * 240);
      const seaSig = `${Math.round(cur.pan.x)}|${Math.round(cur.pan.y)}|${cur.zoom}|${cur.groundTiles.length}|${W}|${H}|${tint}`;
      if (!seaLayer.current)
        seaLayer.current = document.createElement("canvas");
      if (seaKey.current !== seaSig) {
        const hw = (ISO_W * S) / 2,
          hh = (ISO_H * S) / 2;
        const tilePolys = cur.groundTiles.map(([gx, gy]) => {
          const { sx, sy } = isoScreen(gx, gy);
          const x = sx * S + camX,
            y = sy * S + camY;
          return [
            { x, y: y - hh },
            { x: x + hw, y },
            { x, y: y + hh },
            { x: x - hw, y },
          ];
        });
        buildSeaDecor(seaLayer.current, tilePolys, W, H, S, M.sea);
        seaKey.current = seaSig;
      }
      ctx.save();
      ctx.translate(-camX, -camY);
      ctx.globalAlpha = 0.92 + Math.sin(t * 1.3) * 0.08;
      ctx.drawImage(seaLayer.current, 0, 0);
      ctx.restore();

      const alive = new Set();
      for (const p of cur.island.placed) {
        const item = BY_ID[p.itemId];
        if (item?.layer !== "pnj" && item?.layer !== "animal") continue;
        alive.add(p.uid);
        if (!walkers.current.has(p.uid))
          walkers.current.set(
            p.uid,
            makeWalker(p.x, p.y, item.layer === "animal" ? "animal" : "pnj"),
          );
      }
      for (const uid of [...walkers.current.keys()])
        if (!alive.has(uid)) walkers.current.delete(uid);
      if (!reduce)
        updateAgents(
          walkers.current,
          Math.min(0.05, (now - lastNow) / 1000),
          cur.nav,
        );
      lastNow = now;

      const LAYER = { sol: 0, chemin: 1, bloc: 2, agent: 3 };
      const scene = [];

      const covered = new Set();
      for (const p of cur.island.placed) {
        const item = BY_ID[p.itemId];
        if (!item || item.kind !== "tile" || item.layer !== "sol") continue;
        for (const part of partsOf(item, p.flip))
          covered.add(key(p.x + part.dx, p.y + part.dy));
      }

      for (const [gx, gy] of cur.groundTiles) {
        if (covered.has(key(gx, gy))) continue;
        const g =
          cur.island.terrain?.[key(gx, gy)] ||
          (hash(gx, gy) > 0.86 ? "grass" : "dirt");
        const def = GROUND_TYPES.find((d) => d.id === g) || GROUND_TYPES[0];
        const ovg = overrideFor(`ground_${def.id}`);
        scene.push({
          d: gx + gy,
          l: LAYER.sol,
          kind: "tile",
          gx,
          gy,
          mul: ovg ? ovg.scale || 1 : 1,
          img: ovg ? overrideImage(`ground_${def.id}`) : loadTile(def.tile),
          anchor: ovg ? ovg.anchorY : def.tile,
        });
      }

      for (const p of cur.island.placed) {
        const item = BY_ID[p.itemId];
        if (!item) continue;
        if (item.layer === "pnj" || item.layer === "animal") {
          const w = walkers.current.get(p.uid);
          if (!w) continue;
          scene.push({
            d: w.gx + w.gy + 0.85,
            l: LAYER.agent,
            kind: "agent",
            item,
            w,
            uid: p.uid,
          });
          continue;
        }
        if (item.kind === "proc") {
          scene.push({
            d: p.x + p.y,
            l: LAYER.bloc,
            kind: "proc",
            gx: p.x,
            gy: p.y,
            item,
            uid: p.uid,
            flip: p.flip,
          });
          continue;
        }
        pushItem(scene, LAYER, item, p.x, p.y, p.flip, { uid: p.uid });
      }

      if (cur.editMode === "decor" && cur.pending && cur.hover) {
        const it = cur.pending;
        const ok = cur.canPlaceAt(cur.hover.tx, cur.hover.ty);
        if (it.kind === "proc") {
          scene.push({
            d: cur.hover.tx + cur.hover.ty,
            l: LAYER.bloc,
            kind: "ghostProc",
            gx: cur.hover.tx,
            gy: cur.hover.ty,
            item: it,
            ok,
            flip: cur.pendingFlip,
          });
        } else {
          pushItem(
            scene,
            LAYER,
            it,
            cur.hover.tx,
            cur.hover.ty,
            cur.pendingFlip,
            { ghost: true, ok, l: LAYER.bloc },
          );
        }
      }

      for (const b of cur.bridges) {
        const midD =
          b.axis === "x" ? (b.x0 + b.x1) / 2 + b.ya : (b.y0 + b.y1) / 2 + b.xa;
        scene.push({ d: midD, l: LAYER.bloc, kind: "bridge", bridge: b });
      }

      scene.sort((a, b) => a.d - b.d || a.l - b.l);

      const lamps = [];
      for (const o of scene) {
        if (o.kind === "bridge") {
          const b = o.bridge;
          const ba = bobOf(b.ca, t),
            bb = bobOf(b.cb, t);
          const corners =
            b.axis === "x"
              ? [
                  proj(b.x0, b.ya, ba),
                  proj(b.x1, b.ya, bb),
                  proj(b.x1, b.yb, bb),
                  proj(b.x0, b.yb, ba),
                ]
              : [
                  proj(b.xa, b.y0, ba),
                  proj(b.xa, b.y1, bb),
                  proj(b.xb, b.y1, bb),
                  proj(b.xb, b.y0, ba),
                ];
          drawIsoBridge(ctx, corners, S);
          continue;
        }

        const cell = {
          x: Math.floor((o.gx ?? o.w.gx) / STRIDE),
          y: Math.floor((o.gy ?? o.w.gy) / STRIDE),
        };
        const bob = bobOf(cell, t);

        if (o.kind === "agent") {
          const pt = proj(o.w.gx, o.w.gy, bob);
          o.item.draw(ctx, pt.x, pt.y, S * 0.9, t, o.w.facing, o.w.moving);
          if (o.w.bubble && o.w.speaking)
            drawBubble(ctx, pt.x, pt.y, S * 0.9, o.w.bubble, t);
          if (cur.selectedPlaced === o.uid) ring(ctx, pt.x, pt.y, 22 * S);
          continue;
        }

        const pt = proj(o.gx, o.gy, bob);
        const top = { x: pt.x, y: pt.y - (o.lift || 0) * S };

        if (o.kind === "proc" || o.kind === "ghostProc") {
          ctx.save();
          if (o.kind === "ghostProc") ctx.globalAlpha = 0.7;
          mirrored(ctx, pt.x, o.flip, () =>
            o.item.draw(ctx, pt.x, pt.y, S * 0.9, t, 1),
          );
          ctx.restore();
          if (o.item.emitsLight)
            lamps.push({ x: pt.x, y: pt.y - 58 * S * 0.9 });
          if (o.kind === "ghostProc")
            diamond(ctx, pt.x, pt.y, S, o.ok ? "#4ade80" : "#f87171", 3 * S);
          else if (cur.selectedPlaced === o.uid)
            diamond(ctx, pt.x, pt.y, S, "#ffffff", 3 * S);
          continue;
        }

        const { dx, dy, dw, dh } = drawPos(
          top.x,
          top.y,
          S,
          o.anchor,
          o.mul || 1,
        );
        const img = o.img;
        if (o.ghost) {
          ctx.save();
          ctx.globalAlpha = 0.7;
          mirrored(ctx, pt.x, o.flip, () => draw(ctx, img, dx, dy, dw, dh));
          ctx.restore();
          diamond(ctx, pt.x, pt.y, S, o.ok ? "#4ade80" : "#f87171", 3 * S);
          continue;
        }
        if (o.item?.tile === "tree" || o.item?.tile === "trees") {
          const ph = o.uid ? o.uid.charCodeAt(0) : 0;
          const sway = Math.sin(t * 1.1 + ph) * 0.016;
          ctx.save();
          ctx.translate(top.x, top.y);
          ctx.rotate(sway);
          ctx.translate(-top.x, -top.y);
          mirrored(ctx, pt.x, o.flip, () => draw(ctx, img, dx, dy, dw, dh));
          ctx.restore();
        } else {
          mirrored(ctx, pt.x, o.flip, () => draw(ctx, img, dx, dy, dw, dh));
        }

        if (o.l !== LAYER.sol && cur.editMode === "decor") {
          const sel = cur.selectedPlaced === o.uid;
          const hw = (ISO_W * S) / 2,
            hh = (ISO_H * S) / 2;
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(pt.x, pt.y - hh);
          ctx.lineTo(pt.x + hw, pt.y);
          ctx.lineTo(pt.x, pt.y + hh);
          ctx.lineTo(pt.x - hw, pt.y);
          ctx.closePath();
          ctx.fillStyle = sel
            ? "rgba(56,189,248,.40)"
            : "rgba(255,255,255,.16)";
          ctx.fill();
          ctx.strokeStyle = sel ? "#38bdf8" : "rgba(255,255,255,.5)";
          ctx.lineWidth = (sel ? 3 : 2) * S;
          ctx.stroke();
          ctx.restore();
        }
        if (cur.calibrate) diamond(ctx, pt.x, pt.y, S, "#ff2d55", 2.5 * S);
      }

      if (
        cur.editMode === "terrain" &&
        cur.hover &&
        cur.isLand(cur.hover.tx, cur.hover.ty)
      ) {
        const c = {
          x: Math.floor(cur.hover.tx / STRIDE),
          y: Math.floor(cur.hover.ty / STRIDE),
        };
        const pt = proj(cur.hover.tx, cur.hover.ty, bobOf(c, t));
        diamond(ctx, pt.x, pt.y, S, "#38bdf8", 4 * S);
      }

      for (const c of cur.readOnly ? [] : cur.candidates) {
        const poly = chunkPoly(c);
        const p = proj(
          chunkOrigin(c.x, c.y).ox + CH / 2 - 0.5,
          chunkOrigin(c.x, c.y).oy + CH / 2 - 0.5,
        );
        ctx.save();
        ctx.globalAlpha = 0.1 + Math.sin(t * 1.6 + c.x + c.y) * 0.04;
        ctx.fillStyle = "#ffffff";
        ctx.beginPath();
        poly.forEach((q, i) =>
          i ? ctx.lineTo(q.x, q.y) : ctx.moveTo(q.x, q.y),
        );
        ctx.closePath();
        ctx.fill();
        ctx.globalAlpha = 0.8;
        ctx.setLineDash([14 * S, 10 * S]);
        ctx.lineDashOffset = -t * 20 * S;
        ctx.strokeStyle = "rgba(255,255,255,.9)";
        ctx.lineWidth = 3 * S;
        ctx.stroke();
        ctx.restore();

        const affordable =
          cur.chunkCost !== null && cur.balance >= cur.chunkCost;
        const pulse = affordable ? 1 + Math.sin(t * 3) * 0.04 : 1;
        const bw = 168 * S * pulse,
          bh = 54 * S * pulse;
        ctx.save();
        ctx.fillStyle = affordable ? "#f59e0b" : "rgba(15,23,42,.78)";
        ctx.beginPath();
        if (ctx.roundRect)
          ctx.roundRect(p.x - bw / 2, p.y - bh / 2, bw, bh, 14 * S);
        else ctx.rect(p.x - bw / 2, p.y - bh / 2, bw, bh);
        ctx.fill();
        ctx.strokeStyle = "rgba(255,255,255,.85)";
        ctx.lineWidth = 2 * S;
        ctx.stroke();
        ctx.fillStyle = "#fff";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.font = `700 ${Math.max(9, Math.round(15 * S))}px system-ui, sans-serif`;
        ctx.fillText(
          cur.chunkCost === null
            ? tr("isoisland.archipel_complet")
            : tr("isoisland.agrandir_l_ile"),
          p.x,
          p.y - 9 * S,
        );
        ctx.font = `700 ${Math.max(9, Math.round(17 * S))}px system-ui, sans-serif`;
        ctx.fillText(
          cur.chunkCost === null ? "" : `${fmt(cur.chunkCost)} 🪙`,
          p.x,
          p.y + 11 * S,
        );
        ctx.restore();
      }

      ctx.restore();

      if (!reduce) {
        if (birds.current.length < 2 && Math.random() < 0.004) {
          birds.current.push({
            x: -60,
            y: 40 + Math.random() * 120,
            sp: 30 + Math.random() * 40,
            ph: Math.random() * 6,
          });
        }
        ctx.save();
        const bn = M.night;
        ctx.strokeStyle = `rgba(${Math.round(60 + 120 * bn)},${Math.round(60 + 140 * bn)},${Math.round(70 + 160 * bn)},${0.45 + 0.05 * bn})`;
        ctx.lineWidth = 2 * dpr;
        ctx.lineCap = "round";
        birds.current = birds.current.filter((b) => {
          b.x += b.sp * 0.016;
          const flap = Math.sin(t * 8 + b.ph) * 5 * dpr;
          const bx = b.x * dpr,
            by = (b.y + Math.sin(t + b.ph) * 8) * dpr;
          ctx.beginPath();
          ctx.moveTo(bx - 7 * dpr, by + flap);
          ctx.quadraticCurveTo(bx, by - 3 * dpr, bx + 7 * dpr, by + flap);
          ctx.stroke();
          return bx < W + 80;
        });
        ctx.restore();
      }

      for (let i = 0; i < 3; i++) {
        const cx = ((t * (24 + i * 8) + i * 700) % (W + 1000)) - 500;
        drawCloud(ctx, cx, (55 + i * 88) * dpr, 2.6 * S, 0.7 - 0.44 * M.night);
      }

      if (M.grade && M.gradeAlpha > 0.004) {
        const gr = ctx.createLinearGradient(0, 0, 0, H);
        gr.addColorStop(0, M.grade[0]);
        gr.addColorStop(1, M.grade[1]);
        ctx.save();
        ctx.globalCompositeOperation = "multiply";
        ctx.globalAlpha = M.gradeAlpha;
        ctx.fillStyle = gr;
        ctx.fillRect(0, 0, W, H);
        ctx.restore();
      }

      if (raining) {
        ctx.save();
        ctx.fillStyle = "rgba(20,30,45,0.10)";
        ctx.fillRect(0, 0, W, H);
        ctx.restore();
        drawRain(ctx, rainDrops.current, W, H, Math.min(50, dt), 0.8);
      }
      const hz = ctx.createLinearGradient(0, 0, 0, H * 0.5);
      hz.addColorStop(0, M.haze);
      hz.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = hz;
      ctx.fillRect(0, 0, W, H * 0.5);

      if (lamps.length) {
        gctx.save();
        gctx.translate(camX, camY);
        gctx.globalCompositeOperation = "lighter";
        for (const L of lamps) {
          const r = 190 * S;
          const g1 = gctx.createRadialGradient(L.x, L.y, 0, L.x, L.y, r);
          const a = M.lamp;
          g1.addColorStop(0, `rgba(255,214,140,${0.55 * a})`);
          g1.addColorStop(0.35, `rgba(255,168,80,${0.26 * a})`);
          g1.addColorStop(1, "rgba(255,140,60,0)");
          gctx.fillStyle = g1;
          gctx.beginPath();
          gctx.arc(L.x, L.y, r, 0, Math.PI * 2);
          gctx.fill();
        }
        gctx.restore();
      }

      const vg = ctx.createRadialGradient(
        W / 2,
        H * 0.48,
        Math.min(W, H) * 0.26,
        W / 2,
        H * 0.48,
        Math.max(W, H) * 0.8,
      );
      vg.addColorStop(0, "rgba(0,0,0,0)");
      vg.addColorStop(1, `rgba(8,14,26,${M.vignette})`);
      ctx.fillStyle = vg;
      ctx.fillRect(0, 0, W, H);

      raf = requestAnimationFrame(frame);
    };

    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      clearTimeout(refitTimer);
    };
  }, [ready]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onTouchMove = (e) => {
      if (e.touches.length >= 2) {
        e.preventDefault();
      }
    };
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => el.removeEventListener("touchmove", onTouchMove);
  }, []);

  useEffect(() => () => clearTimeout(warningTimer.current), []);

  function mirrored(ctx, axisX, flip, body) {
    if (!flip) return body();
    ctx.save();
    ctx.translate(axisX, 0);
    ctx.scale(-1, 1);
    ctx.translate(-axisX, 0);
    body();
    ctx.restore();
  }
  function missingTile(ctx, dx, dy, dw, dh) {
    ctx.save();
    ctx.strokeStyle = "#f43f5e";
    ctx.setLineDash([4, 4]);
    ctx.lineWidth = 1.5;
    ctx.strokeRect(dx, dy, dw, dh);
    ctx.restore();
  }
  function draw(ctx, img, dx, dy, dw, dh) {
    if (!tileOk(img)) {
      missingTile(ctx, dx, dy, dw, dh);
      return;
    }
    ctx.drawImage(img, dx, dy, dw, dh);
  }
  function diamond(ctx, x, y, S, color, lw) {
    const hw = (ISO_W * S) / 2,
      hh = (ISO_H * S) / 2;
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = lw;
    ctx.beginPath();
    ctx.moveTo(x, y - hh);
    ctx.lineTo(x + hw, y);
    ctx.lineTo(x, y + hh);
    ctx.lineTo(x - hw, y);
    ctx.closePath();
    ctx.stroke();
    ctx.restore();
  }
  function ring(ctx, x, y, r) {
    ctx.save();
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 3;
    ctx.setLineDash([6, 5]);
    ctx.beginPath();
    ctx.ellipse(x, y, r, r * 0.45, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  const clampPan = (next, z = zoom) => {
    if (!island.chunks.length || !wrapRef.current) return next;
    const r = wrapRef.current.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) return next;

    const b = islandBounds(island.chunks);
    const minX = b.minX * z,
      maxX = b.maxX * z;
    const minY = b.minY * z,
      maxY = b.maxY * z;

    const KEEP = Math.min(140, r.width * 0.35, r.height * 0.35);
    const camY0 = r.height * 0.32;

    let loX = KEEP - r.width / 2 - maxX;
    let hiX = r.width - KEEP - r.width / 2 - minX;
    let loY = KEEP - camY0 - maxY;
    let hiY = r.height - KEEP - camY0 - minY;

    if (loX > hiX) {
      const m = (loX + hiX) / 2;
      loX = hiX = m;
    }
    if (loY > hiY) {
      const m = (loY + hiY) / 2;
      loY = hiY = m;
    }

    return {
      x: Math.max(loX, Math.min(hiX, next.x)),
      y: Math.max(loY, Math.min(hiY, next.y)),
    };
  };

  const zoomAround = useCallback(
    (factor, clientX, clientY) => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const px = clientX == null ? r.width / 2 : clientX - r.left;
      const py = clientY == null ? r.height * 0.32 : clientY - r.top;
      setZoom((z) => {
        const z2 = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z * factor));
        setPan((p) => {
          const sx = (px - r.width / 2 - p.x) / z;
          const sy = (py - r.height * 0.32 - p.y) / z;
          return clampPan(
            {
              x: px - r.width / 2 - sx * z2,
              y: py - r.height * 0.32 - sy * z2,
            },
            z2,
          );
        });
        return z2;
      });
    },
    [clampPan],
  );

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      zoomAround(Math.exp(-e.deltaY * 0.0016), e.clientX, e.clientY);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoomAround]);

  const toggleFullscreen = () => {
    const el = shellRef.current;
    if (!el) return;
    if (!document.fullscreenElement)
      el.requestFullscreen?.().catch(() => setFullscreen((f) => !f));
    else document.exitFullscreen?.();
  };
  useEffect(() => {
    const onChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  useEffect(() => {
    onFullscreenChange?.(fullscreen);
  }, [fullscreen, onFullscreenChange]);

  const tileAt = (e) => {
    const r = wrapRef.current.getBoundingClientRect();
    const x = e.clientX - r.left - (r.width / 2 + pan.x);
    const y = e.clientY - r.top - (r.height * 0.32 + pan.y);
    return screenToTile(x / zoom, y / zoom);
  };

  const warnTwoFingers = () => {
    setShowTouchWarning(true);
    clearTimeout(warningTimer.current);
    warningTimer.current = setTimeout(() => setShowTouchWarning(false), 2000);
  };

  const onDown = (e) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    const isTouch = e.pointerType === "touch";

    // Un seul doigt : on n'annule plus le geste. On le traite comme un tap
    // potentiel (pose / selection / achat de chunk). Le message "2 doigts"
    // n'apparait que si le doigt se met a glisser (tentative de pan).
    if (isTouch && pointers.current.size === 1) {
      drag.current = null;
      tap.current = { x: e.clientX, y: e.clientY, ok: true };
      if (editMode) setHover(tileAt(e));
      return;
    }

    tap.current = null;
    setShowTouchWarning(false);

    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = { dist: Math.hypot(a.x - b.x, a.y - b.y) };
      drag.current = {
        sx: (a.x + b.x) / 2,
        sy: (a.y + b.y) / 2,
        ox: pan.x,
        oy: pan.y,
        moved: false,
      };
      return;
    }

    drag.current = {
      sx: e.clientX,
      sy: e.clientY,
      ox: pan.x,
      oy: pan.y,
      moved: false,
    };
  };

  const onMove = (e) => {
    if (pointers.current.has(e.pointerId))
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // Doigt unique en mouvement : pas de pan, mais on laisse un petit
    // ajustement du ghost avant de lacher. Au-dela du seuil -> avertissement
    // et le tap est invalide.
    if (
      e.pointerType === "touch" &&
      pointers.current.size === 1 &&
      tap.current
    ) {
      const dx = e.clientX - tap.current.x;
      const dy = e.clientY - tap.current.y;
      if (Math.abs(dx) + Math.abs(dy) > 12) {
        tap.current.ok = false;
        if (!showTouchWarning) warnTwoFingers();
      } else if (editMode) {
        setHover(tileAt(e));
      }
      return;
    }

    if (pointers.current.size === 2 && pinch.current) {
      const [a, b] = [...pointers.current.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);

      if (pinch.current.dist > 4) {
        zoomAround(d / pinch.current.dist, (a.x + b.x) / 2, (a.y + b.y) / 2);
      }
      pinch.current.dist = d;

      if (drag.current) {
        const mx = (a.x + b.x) / 2,
          my = (a.y + b.y) / 2;
        const dx = mx - drag.current.sx,
          dy = my - drag.current.sy;
        if (Math.abs(dx) + Math.abs(dy) > 4) {
          drag.current.moved = true;
          setPan(
            clampPan({ x: drag.current.ox + dx, y: drag.current.oy + dy }),
          );
        }
      }
      return;
    }

    if (drag.current && e.pointerType !== "touch") {
      const dx = e.clientX - drag.current.sx,
        dy = e.clientY - drag.current.sy;
      if (Math.abs(dx) + Math.abs(dy) > 6) {
        drag.current.moved = true;
        setPan(clampPan({ x: drag.current.ox + dx, y: drag.current.oy + dy }));
      }
    }
    if (editMode && !drag.current?.moved) setHover(tileAt(e));
  };

  const endPointer = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (pointers.current.size === 0) tap.current = null;
  };

  const onUp = (e) => {
    const wasPinch = pointers.current.size >= 2 || !!pinch.current;
    const tapOk = e.pointerType === "touch" ? !!tap.current?.ok : true;
    tap.current = null;
    endPointer(e);
    const wasDrag = drag.current?.moved;
    drag.current = null;

    if (wasDrag || wasPinch || readOnly || !tapOk) return;

    const { tx, ty } = tileAt(e);

    for (const c of candidates) {
      const { ox, oy } = chunkOrigin(c.x, c.y);
      if (tx >= ox - 1 && tx <= ox + CH && ty >= oy - 1 && ty <= oy + CH)
        return onBuyChunk(c);
    }
    if (!editMode) return;
    if (editMode === "terrain") {
      if (isLand(tx, ty)) onPaint(tx, ty, paintGround);
      return;
    }

    let hitUid =
      occupied.objets.get(key(tx, ty)) || occupied.sols.get(key(tx, ty));
    if (!hitUid)
      for (const [uid2, w] of walkers.current)
        if (Math.abs(w.gx - tx) < 1 && Math.abs(w.gy - ty) < 1) {
          hitUid = uid2;
          break;
        }

    if (pending) {
      if (canPlaceAt(tx, ty)) {
        onPlace(tx, ty);
        return;
      }
      if (hitUid) return onSelectPlaced(hitUid, { x: e.clientX, y: e.clientY });
      return;
    }
    if (hitUid) return onSelectPlaced(hitUid, { x: e.clientX, y: e.clientY });
    onSelectPlaced(null);
  };

  const onContext = (e) => {
    e.preventDefault();
    if (readOnly) return;
    if (pending || selectedPlaced) return onCancelAction?.();
    if (editMode) return onExitEditor?.();
    onCancelAction?.();
  };

  const btn =
    "w-9 h-9 grid place-items-center rounded-xl bg-black/45 hover:bg-black/70 text-white backdrop-blur transition shadow-lg";

  return (
    <div
      ref={shellRef}
      className={`relative ${fullscreen ? "fixed inset-0 z-[200] bg-[#123a58]" : ""}`}
    >
      {showTouchWarning && (
        <div className="absolute inset-0 z-[50] flex items-center justify-center pointer-events-none">
          <div className="bg-black/70 text-white text-xs sm:text-sm font-semibold px-4 py-2.5 rounded-full backdrop-blur-md shadow-xl transition-opacity animate-in fade-in zoom-in duration-200">
            {t("isoisland.utilisez_2_doigts_pour_naviguer")}
          </div>
        </div>
      )}

      <div
        ref={wrapRef}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={endPointer}
        onPointerLeave={(e) => {
          endPointer(e);
          drag.current = null;
          setHover(null);
        }}
        onContextMenu={onContext}
        onDragOver={(e) => {
          e.preventDefault();
          setHover(tileAt(e));
        }}
        onDragLeave={() => setHover(null)}
        onDrop={(e) => {
          e.preventDefault();
          const id = e.dataTransfer.getData("text/oasis-item");
          if (!id) return;
          const { tx, ty } = tileAt(e);
          onDropItem?.(id, tx, ty);
          setHover(null);
        }}
        className={`relative overflow-hidden select-none cursor-grab active:cursor-grabbing bg-[#123a58] ${
          fullscreen
            ? "h-screen w-screen"
            : "h-[68vh] sm:h-[80vh] min-h-[380px] sm:min-h-[600px] max-h-[960px] w-full"
        }`}
      >
        {!ready && (
          <div className="absolute inset-0 grid place-items-center text-sky-100 text-sm animate-pulse">
            {t("isoisland.chargement_de_l_oasis")}
          </div>
        )}
        <canvas ref={canvasRef} className="absolute inset-0" />
        <canvas
          ref={glowRef}
          className="absolute inset-0 pointer-events-none mix-blend-screen"
        />
      </div>

      <div className="absolute top-3 right-3 flex flex-col gap-1.5 z-[20]">
        <button
          type="button"
          onClick={toggleFullscreen}
          title={t("isoisland.plein_ecran")}
          className={btn}
        >
          {fullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </button>
        <button
          type="button"
          title={t("isoisland.zoomer")}
          onClick={() => zoomAround(1.25)}
          className={`${btn} font-bold`}
        >
          +
        </button>
        <button
          type="button"
          title={t("isoisland.dezoomer")}
          onClick={() => zoomAround(1 / 1.25)}
          className={`${btn} font-bold`}
        >
          −
        </button>
        <button
          type="button"
          onClick={fitToIsland}
          title={t("isoisland.recadrer_sur_l_ile")}
          className={btn}
        >
          <Move size={16} />
        </button>
        <div className="text-[10px] text-white/80 text-center tabular-nums bg-black/40 rounded-lg py-0.5 backdrop-blur">
          ×{zoom.toFixed(2)}
        </div>
      </div>

      {!readOnly && (
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 z-[20]">
          <button
            type="button"
            onClick={() => onToggleEdit?.()}
            title={
              editMode
                ? t("isoisland.terminer_l_amenagement")
                : t("isoisland.amenager")
            }
            className={`w-11 h-11 grid place-items-center rounded-2xl backdrop-blur transition shadow-lg ${
              editMode
                ? "bg-emerald-500 text-white"
                : "bg-black/45 hover:bg-black/70 text-white"
            }`}
          >
            {editMode ? <Check size={18} /> : <Pencil size={18} />}
          </button>
          {pending && (
            <button
              type="button"
              onClick={() => onFlipPending?.()}
              title={t("isoisland.pivoter_miroir_gauche_droite_touche_r")}
              className={`w-11 h-11 grid place-items-center rounded-2xl backdrop-blur transition shadow-lg ${
                pendingFlip
                  ? "bg-sky-500 text-white"
                  : "bg-black/45 hover:bg-black/70 text-white"
              }`}
            >
              <FlipHorizontal2 size={18} />
            </button>
          )}
        </div>
      )}

      {overlay}
    </div>
  );
}
